import { Inject, inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { from } from 'rxjs';
import { filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { AppActions } from '../../../core/store/actions/app';
import { useToaster } from '../../../shared/composables/use-toaster';
import { VideoCallToastActionComponent } from '../../components/video-call-toast-action/video-call-toast-action.component';
import { VideoCall } from '../../models/video-call';
import { VideoCallModal } from '../../services/video-call-modal';
import { VIDEO_CALL_GATEWAY, VideoCallGateway } from '../../services/VideoCallGateway';
import { VideoCallProvider } from '../../services/VideoCallProvider';
import { getVideoCallToasterId } from '../../utils/getVideoCallToasterId';
import { VideoCallsActions } from '../actions/videoCall';

@Injectable()
export class VideoCallEffects {
    private readonly toaster = useToaster();
    private readonly translateService = inject(TranslateService);

    loadCurrentVideoCalls$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.initialized),
            switchMap(() =>
                this.videoCallGateway
                    .getVideoCalls()
                    .pipe(map((videoCalls: VideoCall[]) => VideoCallsActions.videoCallsLoaded({ videoCalls }))),
            ),
        ),
    );

    showToasterMessageOnInvitationSent$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(VideoCallsActions.videoCallCreated, VideoCallsActions.videoCallInvitationSent),
                tap(() =>
                    this.toaster.success({
                        title: this.translateService.instant('TOASTER_VIDEO_CALL_INVITATION_SENT_MESSAGE'),
                    }),
                ),
            ),
        { dispatch: false },
    );

    showToasterMessageOnParticipantJoined$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(VideoCallsActions.videoCallParticipantJoined),
                filter(({ isOwner }) => !isOwner),
                tap(({ siteId, userName }) =>
                    this.toaster.info({
                        id: getVideoCallToasterId(siteId),
                        title: this.translateService.instant('GUEST_JOINED_VIDEO_CALL', { userName }),
                        duration: 0,
                        component: VideoCallToastActionComponent,
                        componentInputs: { siteId },
                    }),
                ),
            ),
        { dispatch: false },
    );

    dismissToasterOnParticipantLeft$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(VideoCallsActions.videoCallParticipantLeft),
                filter(({ isOwner }) => !isOwner),
                tap(({ siteId }) => this.toaster.dismiss(getVideoCallToasterId(siteId))),
            ),
        { dispatch: false },
    );

    closeVideoCallAfterRequested$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(VideoCallsActions.videoCallStoppedRequested),
                mergeMap(({ siteId }) =>
                    from(this.videoCallProvider.hungUpVideoCall()).pipe(
                        switchMap(() => this.videoCallGateway.stopVideoCall(siteId)),
                        tap(() => {
                            this.videoCallModal.close();
                        }),
                    ),
                ),
            ),
        { dispatch: false },
    );

    constructor(
        private actions$: Actions,
        @Inject(VIDEO_CALL_GATEWAY) private videoCallGateway: VideoCallGateway,
        @Inject('VideoCallProvider') private videoCallProvider: VideoCallProvider,
        private videoCallModal: VideoCallModal,
    ) {}
}
